/**
 * Fase 22 — Dispatcher de impressão: escolhe Web Serial (ESC/POS) ou Windows (HTML).
 *
 * Lê `config.printMode` do Dexie:
 *   - 'windows' (padrão): usa printHTML via iframe → driver Windows da Jetway.
 *   - 'webserial': usa Web Serial API → porta COM virtual (CH340/FTDI).
 *
 * Expõe helpers que já fazem o switch: printCommandTicket, printCommandReceipt, etc.
 * Cada um tem fallback: se o modo preferido falhar, tenta o outro silenciosamente
 * para não travar a operação (ex: "Comanda salva mesmo sem impressão").
 */
import db from '@/db';
import {
  generateCommandTicket, generateCommandReceipt, generateKitchenLabel, generateDeliveryLabel,
  connectPrinter, printData,
} from '@/lib/printer';
import {
  printHTML,
  commandTicketHTML, commandReceiptHTML, kitchenLabelHTML, deliveryLabelHTML, testPrintHTML,
} from '@/lib/printerWindows';
import { getPixQRDataURL } from '@/lib/pixQR';

const DEFAULT_MODE = 'windows'; // ← padrão novo: funciona com driver Jetway do Windows

export async function getPrintMode() {
  try {
    const row = await db.config.get('printMode');
    const v = row?.value;
    if (v === 'webserial' || v === 'windows') return v;
    return DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

export async function setPrintMode(mode) {
  if (mode !== 'windows' && mode !== 'webserial') {
    throw new Error('Modo inválido');
  }
  await db.config.put({ key: 'printMode', value: mode });
  return mode;
}

// ─── WEB SERIAL (ESC/POS bytes) ──────────────────────────────────
async function viaWebSerial(bytes) {
  const port = await connectPrinter({ interactive: true });
  await printData(port, bytes);
}

// ─── DISPATCHERS POR TIPO DE CUPOM ───────────────────────────────

export async function printCommandTicket(command, lastEntry, businessName = 'Kaven Restaurante') {
  const mode = await getPrintMode();
  if (mode === 'webserial') {
    await viaWebSerial(generateCommandTicket(command, lastEntry, businessName));
    return { ok: true, mode };
  }
  await printHTML(commandTicketHTML(command, lastEntry, businessName), `Comanda ${command.number}`);
  return { ok: true, mode };
}

export async function printCommandReceipt(command, businessName = 'Kaven Restaurante') {
  const mode = await getPrintMode();
  if (mode === 'webserial') {
    await viaWebSerial(generateCommandReceipt(command, businessName));
    return { ok: true, mode };
  }
  const pixQR = await getPixQRDataURL();
  await printHTML(commandReceiptHTML(command, businessName, pixQR), `Extrato ${command.number}`);
  return { ok: true, mode };
}

export async function printKitchenLabel(order) {
  const mode = await getPrintMode();
  if (mode === 'webserial') {
    await viaWebSerial(generateKitchenLabel(order));
    return { ok: true, mode };
  }
  await printHTML(kitchenLabelHTML(order), `Cozinha #${order.id}`);
  return { ok: true, mode };
}

export async function printDeliveryLabel(order) {
  const mode = await getPrintMode();
  if (mode === 'webserial') {
    await viaWebSerial(generateDeliveryLabel(order));
    return { ok: true, mode };
  }
  const pixQR = await getPixQRDataURL();
  await printHTML(deliveryLabelHTML(order, pixQR), `Delivery #${order.id}`);
  return { ok: true, mode };
}

export async function printTest() {
  const mode = await getPrintMode();
  if (mode === 'webserial') {
    // Usa o mesmo teste ESC/POS da PrintPage antiga
    const { generateManualComanda } = await import('@/lib/printer');
    await viaWebSerial(generateManualComanda(
      { number: 99 },
      [
        { quantity: 1, name: 'TESTE DE IMPRESSAO' },
        { quantity: 1, name: 'Jetway JP-500 OK' },
        { quantity: 1, name: new Date().toLocaleString('pt-BR') },
      ],
      'Kaven Restaurante'
    ));
    return { ok: true, mode };
  }
  await printHTML(testPrintHTML(), 'Teste de Impressão');
  return { ok: true, mode };
}

/**
 * Auto-print silencioso (usado pelo KDS quando entra delivery novo).
 * - Modo 'webserial': imprime direto se porta autorizada (sem prompt).
 * - Modo 'windows': NÃO imprime automaticamente (evita abrir diálogo intrusivo
 *   a cada pedido novo). O cozinheiro clica "Imprimir Ficha" no card do KDS.
 * Nunca lança: falha vira { ok: false, reason }.
 */
export async function autoPrintKitchenLabel(order) {
  try {
    const mode = await getPrintMode();
    if (mode === 'webserial') {
      const { tryAutoPrint } = await import('@/lib/printer');
      const r = await tryAutoPrint(generateKitchenLabel(order));
      return { ...r, mode };
    }
    // Modo windows: pula auto-print (seria intrusivo). Retorna skip.
    return { ok: false, reason: 'windows_mode_manual_only', mode };
  } catch (err) {
    return { ok: false, reason: 'error', error: String(err?.message || err) };
  }
}
